import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Box,
  Typography,
  Container,
  Stack,
  Button,
  Alert,
  Paper,
  CircularProgress,
} from '@mui/material'
import {
  Refresh as RefreshIcon,
  CloudUpload as ImportIcon,
  CloudDownload as ExportIcon,
  ViewColumn as ColumnIcon,
} from '@mui/icons-material'
import { useDatabase } from '@/hooks/useDatabase'
import { DatabaseTable } from '@/components/Database/DatabaseTable'
import { ExportDialog } from '@/components/Database/ExportDialog'
import { ImportDialog } from '@/components/Database/ImportDialog'
import { ColumnManager } from '@/components/Database/ColumnManager'
import { DuplicateWarning } from '@/components/InvoiceAnalyser/DuplicateWarning'
import type { DatabaseInvoice, ColumnDefinition, DatabaseStats } from '@/types/database'

export default function DatabasePage() {
  const [searchParams] = useSearchParams()
  const {
    getInvoices,
    getStats,
    deleteInvoice,
    updateInvoice,
    createInvoice,
    getColumns,
    createColumn,
    deleteColumn,
    exportInvoices,
    importFromExcel,
    checkDuplicate,
  } = useDatabase()

  const [invoices, setInvoices] = useState<DatabaseInvoice[]>([])
  const [columns, setColumns] = useState<ColumnDefinition[]>([])
  const [stats, setStats] = useState<DatabaseStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Dialog states
  const [exportOpen, setExportOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [columnManagerOpen, setColumnManagerOpen] = useState(false)
  const [duplicateOpen, setDuplicateOpen] = useState(false)
  const [pendingInvoice, setPendingInvoice] = useState<Partial<DatabaseInvoice> | null>(null)
  const [duplicateInfo, setDuplicateInfo] = useState<{
    supplierName: string
    rechnungsnummer: string
    dateInvoice: string | null
    duplicateId: string | null
  } | null>(null)

  // Pagination state
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [totalCount, setTotalCount] = useState(0)
  const highlightId = searchParams.get('invoice')

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [invoicesData, statsData] = await Promise.all([
        getInvoices({ page, limit: pageSize }),
        getStats(),
      ])
      setInvoices(invoicesData.data)
      setTotalCount(invoicesData.total)
      setStats(statsData)
    } catch (err) {
      console.error('Failed to load data:', err)
      setError(err instanceof Error ? err.message : 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }, [getInvoices, getStats, page, pageSize])

  // Load columns
  const loadColumns = useCallback(async () => {
    try {
      const data = await getColumns()
      setColumns(data)
    } catch (err) {
      console.error('Failed to load columns:', err)
      setError(err instanceof Error ? err.message : 'Failed to load columns')
    }
  }, [getColumns])

  useEffect(() => {
    void loadData()
    void loadColumns()
  }, [loadData, loadColumns])

  // Handle invoice update
  const handleUpdateInvoice = useCallback(
    async (id: string, data: Partial<DatabaseInvoice>) => {
      try {
        await updateInvoice(id, data)
        await loadData()
        setSuccessMessage('Invoice updated successfully')
        setTimeout(() => setSuccessMessage(null), 3000)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update invoice')
      }
    },
    [updateInvoice, loadData]
  )

  // Handle invoice delete
  const handleDeleteInvoice = useCallback(
    async (id: string) => {
      try {
        await deleteInvoice(id)
        await loadData()
        setSuccessMessage('Invoice deleted successfully')
        setTimeout(() => setSuccessMessage(null), 3000)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete invoice')
      }
    },
    [deleteInvoice, loadData]
  )

  // Separate function for actual invoice creation
  const actuallyCreateInvoice = useCallback(
    async (data: Partial<DatabaseInvoice>) => {
      try {
        await createInvoice({
          supplierName: data.supplierName || '',
          rechnungsnummer: data.rechnungsnummer || '',
          dateInvoice: data.dateInvoice || null,
          brutto: data.brutto ?? null,
          netto: data.netto ?? null,
          comment: data.comment || '',
          currency: data.currency || null,
          projectName: data.projectName || null,
          recipient: data.recipient || null,
          ustId: data.ustId || null,
          confidence: data.confidence || null,
          source: 'dashboard',
          customFields: data.customFields || {},
        })
        await loadData()
        setSuccessMessage('Invoice created successfully')
        setTimeout(() => setSuccessMessage(null), 3000)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create invoice')
      }
    },
    [createInvoice, loadData]
  )

  // Handle create invoice
  const handleCreateInvoice = useCallback(
    async (data: Partial<DatabaseInvoice>) => {
      const supplierName = data.supplierName || ''
      const rechnungsnummer = data.rechnungsnummer || ''
      const dateInvoice = data.dateInvoice || null

      // Check for duplicates first
      try {
        const dupResult = await checkDuplicate({
          supplierName,
          rechnungsnummer,
        })

        if (dupResult.isDuplicate) {
          // Store pending invoice and show warning dialog
          setPendingInvoice(data)
          setDuplicateInfo({
            supplierName,
            rechnungsnummer,
            dateInvoice,
            duplicateId: dupResult.duplicateIds[0] || null,
          })
          setDuplicateOpen(true)
          return
        }
      } catch (error) {
        console.error('Duplicate check failed:', error)
        // Continue with creation on error
      }

      // Actually create the invoice
      await actuallyCreateInvoice(data)
    },
    [actuallyCreateInvoice, checkDuplicate]
  )

  // Handle duplicate warning confirm
  const handleDuplicateConfirm = useCallback(() => {
    setDuplicateOpen(false)
    if (pendingInvoice) {
      void actuallyCreateInvoice(pendingInvoice)
      setPendingInvoice(null)
    }
  }, [pendingInvoice, actuallyCreateInvoice])

  // Handle duplicate warning cancel
  const handleDuplicateCancel = useCallback(() => {
    setDuplicateOpen(false)
    setPendingInvoice(null)
    setDuplicateInfo(null)
  }, [])

  // Handle export
  const handleExport = useCallback(
    async (config: { format: 'xlsx' | 'csv'; separator: 'comma' | 'period'; includeCustomColumns: boolean }) => {
      try {
        await exportInvoices(config)
        setSuccessMessage('Export successful')
        setTimeout(() => setSuccessMessage(null), 3000)
        setExportOpen(false)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Export failed')
      }
    },
    [exportInvoices]
  )

  // Handle import
  const handleImport = useCallback(
    async (file: File) => {
      try {
        const result = await importFromExcel(file)
        await loadData()
        setSuccessMessage(`${result.imported} invoices imported`)
        setTimeout(() => setSuccessMessage(null), 3000)
        setImportOpen(false)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Import failed')
      }
    },
    [importFromExcel, loadData]
  )

  const handleCreateColumn = useCallback(async (name: string, type: 'text' | 'number' | 'date') => {
    await createColumn(name, type)
    await loadColumns()
    setSuccessMessage('Column created successfully')
    setTimeout(() => setSuccessMessage(null), 3000)
  }, [createColumn, loadColumns])

  const handleDeleteColumn = useCallback(async (id: string) => {
    await deleteColumn(id)
    await loadColumns()
    setSuccessMessage('Column deleted successfully')
    setTimeout(() => setSuccessMessage(null), 3000)
  }, [deleteColumn, loadColumns])

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h4">Database</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadData}
            disabled={loading}
            variant="outlined"
            size="small"
          >
            Refresh
          </Button>
          <Button
            startIcon={<ColumnIcon />}
            onClick={() => setColumnManagerOpen(true)}
            variant="outlined"
            size="small"
          >
            Columns
          </Button>
          <Button
            startIcon={<ImportIcon />}
            onClick={() => setImportOpen(true)}
            variant="outlined"
            size="small"
          >
            Import
          </Button>
          <Button
            startIcon={<ExportIcon />}
            onClick={() => setExportOpen(true)}
            variant="outlined"
            size="small"
          >
            Export
          </Button>
        </Stack>
      </Stack>

      {stats && (
        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="caption" color="textSecondary">Total Invoices</Typography>
            <Typography variant="h6">{stats.totalInvoices}</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="caption" color="textSecondary">Dashboard</Typography>
            <Typography variant="h6">{stats.dashboardCount}</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="caption" color="textSecondary">Invoice Analyser</Typography>
            <Typography variant="h6">{stats.analyserCount}</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="caption" color="textSecondary">Database Size</Typography>
            <Typography variant="h6">{(stats.dbSize / 1024).toFixed(2)} KB</Typography>
          </Paper>
        </Stack>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
      <DatabaseTable
        invoices={invoices}
        columns={columns}
        pageSize={pageSize}
        totalCount={totalCount}
        page={page}
        highlightId={highlightId}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onEdit={handleUpdateInvoice}
        onDelete={handleDeleteInvoice}
        onCreate={handleCreateInvoice}
        />
      )}

      {/* Dialogs */}
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        onExport={handleExport}
      />

      <ImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={handleImport}
      />

      <ColumnManager
        open={columnManagerOpen}
        onClose={() => setColumnManagerOpen(false)}
        columns={columns}
        onCreateColumn={handleCreateColumn}
        onDeleteColumn={handleDeleteColumn}
      />

      {/* Duplicate Warning Dialog */}
      {duplicateInfo && (
        <DuplicateWarning
          open={duplicateOpen}
          supplierName={duplicateInfo.supplierName}
          rechnungsnummer={duplicateInfo.rechnungsnummer}
          dateInvoice={duplicateInfo.dateInvoice}
          duplicateId={duplicateInfo.duplicateId}
          onConfirm={handleDuplicateConfirm}
          onCancel={handleDuplicateCancel}
        />
      )}
    </Container>
  )
}
